// AnnounceBanner — top-of-screen banner driven by SessionView.activeAnnounces
// (Phase 9.6).
//
// 表示位置: MaintenanceBanner と同じ画面上部の絶対配置, ただし maintenance banner
// の **下** に stack する (= maintenance > announces の優先順位、空間も同方向).
// 複数 active を縦に並べる (max 10、server cap と一致).
//
// 振る舞い:
//   - severity で色分け (info=青/シアン、warning=黄、critical=赤). 既存 token に
//     warning/critical の専用色は無いため、Tailwind utility 色で直接当てる.
//   - 各 banner に × ボタン (client-side dismissal only、broadcast 全体は変えない).
//     dismiss 状態は memory のみで、reload で復活 (= localStorage に保存しない).
//   - 残り時間 (= expiresAt の相対時間) は表示しない (= UI 簡素化、TTL は admin 側
//     管理事項で end-user には不要). TTL 切れ自体は client-side safeguard として
//     表示除外する.
//   - active 件数 0 件 / 全て dismiss 済 / TTL 切れのみ → null を返す.

import type { SystemAnnouncePosted } from '@oekaki/protocol';
import { useEffect, useMemo, useState } from 'react';

import { linkifyMessage } from './linkifyMessage.js';

// バナーは severity ごとに背景・文字色が変わる (info=白文字/青, warning=黒文字/黄,
// critical=白文字/赤) ため、リンクは currentColor を継承して下線で示す. これで
// どの severity 配色でも本文と同じ色 + 下線でリンクと判別でき、配色を壊さない.
const BANNER_LINK_CLASS = 'underline underline-offset-2 hover:opacity-80';

export interface AnnounceBannerProps {
  readonly announces: readonly SystemAnnouncePosted[];
  /** テスト時に固定する用. 未指定なら Date.now. */
  readonly now?: () => number;
}

/** 受信時 expiresAt 切れ判定は reducer 側で済んでるが、表示時の保険として再判定. */
const isExpired = (a: SystemAnnouncePosted, nowMs: number): boolean => nowMs >= a.expiresAt;

const SEVERITY_CLASS: Record<NonNullable<SystemAnnouncePosted['severity']>, string> = {
  // info: 青/シアン (accent 系の代替).
  info: 'bg-sky-600/90 text-white border-sky-300/40',
  // warning: 黄/オレンジ.
  warning: 'bg-amber-500/90 text-black border-amber-300/40',
  // critical: 赤 (maintenance と同系の var(--color-error) と整合).
  critical: 'bg-rose-700/90 text-white border-rose-300/40',
};

export const AnnounceBanner = ({ announces, now }: AnnounceBannerProps): React.ReactNode => {
  const clock = now ?? ((): number => Date.now());
  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set());

  // Active id 集合が変わったら、dismissed の中で active に無くなった id を捨てる
  // (= server で取り下げ済の id を memory に残し続けない、メモリリーク防止).
  useEffect(() => {
    setDismissedIds((prev) => {
      const activeIds = new Set(announces.map((a) => a.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (activeIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [announces]);

  const visible = useMemo(() => {
    const nowMs = clock();
    return (
      announces
        .filter((a) => !isExpired(a, nowMs))
        .filter((a) => !dismissedIds.has(a.id))
        // safeguard cap: 念のため UI 側でも 10 件で切る (server cap と整合).
        .slice(0, 10)
    );
  }, [announces, dismissedIds, clock]);

  if (visible.length === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // top-0 だと MaintenanceBanner と重なるので、top-6 (= 約 24px) からスタック.
      // MaintenanceBanner は高さ ~24px なので、その下に並べる位置.
      className="pointer-events-none absolute top-6 right-0 left-0 z-50 flex flex-col gap-1 px-2"
    >
      {visible.map((a) => {
        const severity = a.severity ?? 'info';
        const colorCls = SEVERITY_CLASS[severity];
        return (
          <div
            key={a.id}
            className={`pointer-events-auto flex items-start justify-between gap-2 rounded border px-3 py-1 text-xs font-bold shadow ${colorCls}`}
          >
            <span className="min-w-0 flex-1 break-words whitespace-pre-wrap">
              {linkifyMessage(a.message, { linkClassName: BANNER_LINK_CLASS })}
            </span>
            <button
              type="button"
              aria-label="このお知らせを閉じる"
              className="shrink-0 cursor-pointer rounded px-1.5 text-sm leading-none opacity-70 hover:opacity-100"
              onClick={() => {
                setDismissedIds((prev) => {
                  const next = new Set(prev);
                  next.add(a.id);
                  return next;
                });
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
};
